import { format } from "date-fns";
import { buffer, concat, filter, fromPromise, interval, map, merge, pipe, Source } from "wonka";
import { Override } from "../types";
import { dateToStr, strToDate } from "../utils/dates";
import { removeEmpty } from "../utils/objects";
import { deserialize, upsert } from "../utils/wonka";
import { SubscriptionType } from "./adaptors/ws";
import {
  AssistCompleted,
  Event as EventDto,
  EventColor as EventColorDto,
  EventResponseStatus as EventResponseStatusDto,
  EventSourceDetails,
  EventSubType,
  EventType,
  PriorityLevel as PriorityLevelDto,
  ReclaimEventType as ReclaimEventTypeDto,
} from "./client";
import {
  AssistDetails,
  AssistStatus,
  AssistType,
  Category,
  EventColor,
  EventColorStr,
  PrimaryCategory,
} from "./EventMetaTypes";
import { CalendarEvent as CalendarEventDto } from "./scheduling-links/scheduling-links-client";
import { NotificationKeyStatus, TransformDomain } from "./types";

export enum EventResponseStatus {
  None = "None",
  Organizer = "Organizer",
  Accepted = "Accepted",
  Declined = "Declined",
  TentativelyAccepted = "TentativelyAccepted",
  NotResponded = "NotResponded",
}

export type EventResponseStatusStr = `${EventResponseStatusDto}`;
export type ReclaimEventTypeStr = `${ReclaimEventTypeDto}`;
export type PriorityLevel = `${PriorityLevelDto}`;

export enum ReclaimEventType {
  User = ReclaimEventTypeDto.USER,
  Sync = ReclaimEventTypeDto.SYNC,
  HabitAssignment = ReclaimEventTypeDto.HABITASSIGNMENT,
  OneOnOneAssignment = ReclaimEventTypeDto.ONEONONEASSIGNMENT,
  TaskAssignment = ReclaimEventTypeDto.TASKASSIGNMENT,
  SchedulingLinkAssignment = ReclaimEventTypeDto.SCHEDULINGLINKMEETING,
  ConfBuffer = ReclaimEventTypeDto.CONFBUFFER,
  TravelBuffer = ReclaimEventTypeDto.TRAVELBUFFER,
  Unknown = ReclaimEventTypeDto.UNKNOWN,
}

export enum EventStatus {
  Draft = "DRAFT",
  Published = "PUBLISHED",
  Cancelled = "CANCELLED",
  Rescheduling = "RESCHEDULING",
}

export type EventKey = string;

export { SnoozeOption } from "./client";
export interface EventListOptions {
  start?: Date;
  end?: Date;
  type?: EventType[];
  calendarIds?: number[];
  sourceDetails?: boolean;
  habitIds?: number[];
  oneOnOneIds?: number[];
  schedulingLinkIds?: string[];
}

export interface EventQueryOptions extends EventListOptions {
  statuses?: EventStatus[];
}

export interface WatchQueryOptions extends EventQueryOptions {}
export interface ListAndWatchOptions extends EventQueryOptions {}

export type CalendarEvent = Readonly<
  Override<
    CalendarEventDto,
    {
      eventId: string;
      title: string;
      startTime: Date;
      endTime: Date;
      color: EventColorStr;
      rsvpStatus: EventResponseStatusStr;
      displayColorHex: string;
    }
  >
>;

export const calendarEventToDto = (data: CalendarEvent): CalendarEventDto => ({
  ...data,
  startTime: data.startTime.toISOString(),
  endTime: data.endTime.toISOString(),
  color: EventColorDto[data.color],
  rsvpStatus: EventResponseStatusDto[data.rsvpStatus],
});

export const dtoToCalendarEvent = (dto: CalendarEventDto): CalendarEvent => {
  const { eventId, title, startTime, endTime } = dto;
  if (typeof eventId !== "string") throw new Error("CalendarEvent must have an eventId");
  if (typeof startTime !== "string") throw new Error("CalendarEvent must have an startTime");
  if (typeof endTime !== "string") throw new Error("CalendarEvent must have an endTime");

  const startTimeDate = new Date(startTime);
  const endTimeDate = new Date(endTime);

  if (isNaN(startTimeDate.getTime())) throw new Error("startTime was not a valid ISO date");
  if (isNaN(endTimeDate.getTime())) throw new Error("endTime was not a valid ISO date");

  return {
    ...dto,
    title: title || "",
    eventId,
    startTime: startTimeDate,
    endTime: endTimeDate,
    color: EventColor.get(dto.color).key,
    rsvpStatus: "Accepted",
    // I believe this should always be defined
    displayColorHex: dto.displayColorHex as string,
  };
};

export type Event = Override<
  Omit<EventDto, "projects" | "projectIds">,
  {
    // primary key: *THE* PK for an event is: `event.key`
    readonly key: EventKey;
    readonly reclaimEventType: ReclaimEventType;
    readonly category: Category;
    eventStart: Date;
    eventEnd: Date;
    meetingType?: null;
    color: EventColor;

    /**
     * The following fields are marked as required on the dto object but much of the UI
     * is built with these fields being optional.
     *
     * This is the result of API immutable events: https://github.com/reclaim-ai/reclaim-server/pull/5842
     * Over time we should make these fields as required where it makes sense throughout the app. This
     * mismatch is not much of a concern as events are mostly readonly by design and never get included in
     * update requests as a body.
     */
    readonly assist?: AssistDetails;
    readonly status?: EventStatus;
    // categoryOverride is a write only field to set an overide.
    // Use this ONLY to know if the category has been overridden
    categoryOverride?: Category;
    type?: EventType;
    subType?: EventSubType;
    updated?: Date;
    calendarId?: number;
    priority?: PriorityLevel;
    title?: string;
    titleSeenByOthers?: string;
    location?: string;
    description?: string;
    sourceDetails?: EventSourceDetails;
    timeChunks?: number;
    allocatedTimeChunks?: number;
    recurringEventId?: string;
    recurrence?: string;
    recurrenceMax?: string;
    organizer?: string;
    requiresTravel?: boolean;
    etag?: string | null;
  }
>;

// TODO: CalendarEvent is an eventual replacement for Event.  This function is to bridge the gap -SG
export const calendarEventToEvent = (calendarEvent: CalendarEvent): Event => ({
  ...calendarEvent,
  category: PrimaryCategory.SoloWork,
  key: calendarEvent.eventId,
  reclaimEventType: ReclaimEventType.Unknown,
  rsvpStatus: EventResponseStatus[calendarEvent.rsvpStatus || "Accepted"],
  eventStart: calendarEvent.startTime,
  eventEnd: calendarEvent.endTime,
  color: EventColor.get(calendarEvent.color),
  onlineMeetingUrl: calendarEvent.onlineMeetingUrl || undefined,
});

const eventsQueryFilter = (options: EventQueryOptions): ((events: Event[]) => Event[]) => {
  const { statuses, start, end, habitIds, type, oneOnOneIds, calendarIds } = options;
  return (events: Event[]) => {
    return events
      .filter(
        (e) =>
          (!start || !(e.eventStart <= start && e.eventEnd <= start)) &&
          (!end || !(e.eventStart > end && e.eventEnd > end))
      )
      .filter((e) => !calendarIds || (!!e.calendarId && calendarIds.includes(e.calendarId)))
      .filter((e) => !!e.status && !!statuses && statuses.includes(e.status))
      .filter((e) => !type || (!!e.type && type.includes(e.type)))
      .filter((e) => !habitIds || (!!e.assist?.dailyHabitId && habitIds?.includes(e.assist.dailyHabitId)))
      .filter((e) => !oneOnOneIds || (!!e.assist?.dailyHabitId && oneOnOneIds?.includes(e.assist.dailyHabitId)));
  };
};

export function dtoToEvent(dto: EventDto): Event {
  const assist: AssistDetails | undefined = !!dto.assist
    ? {
        ...dto.assist,
        type: !!dto.assist.type ? AssistType.get(dto.assist.type) : undefined,
        status: dto.assist.status as unknown as AssistStatus,
        dailyHabitId: dto.assist.dailyHabitId || undefined,
        taskId: dto.assist.taskId || undefined,
        taskIndex: dto.assist.taskIndex || undefined,
      }
    : undefined;

  return {
    ...dto,
    assist,
    key: dto.key as unknown as string,
    eventId: dto.eventId || "-",
    reclaimEventType: dto.reclaimEventType as unknown as ReclaimEventType,
    status: dto.status as unknown as EventStatus,
    eventStart: strToDate(dto.eventStart) as Date,
    eventEnd: strToDate(dto.eventEnd) as Date,
    updated: strToDate(dto.updated),
    meetingType: null,
    category: Category.get(dto.category as unknown as string) || PrimaryCategory.TeamMeeting,
    categoryOverride: Category.get(dto.categoryOverride as unknown as string),
    color: !!dto.color ? EventColor.get(dto.color) : EventColor.Auto,
  };
}

export function eventToDto(event: Partial<Event>): EventDto {
  return removeEmpty({
    ...event,
    eventStart: dateToStr(event.eventStart),
    eventEnd: dateToStr(event.eventEnd),
    updated: dateToStr(event.updated),
    category: event.category?.toJSON(),
    categoryOverride: event.categoryOverride?.toJSON(),
    color: (EventColor.Auto === event.color ? null : event.color?.toJSON()) as EventDto["color"],
  }) as EventDto;
}

export function parseEventPriorityLevel(eventPriority?: PriorityLevel): PriorityLevel {
  return eventPriority && eventPriority !== "DEFAULT" && eventPriority !== "PRIORITIZE" ? eventPriority : "P1";
}

const EventsSubscription = {
  subscriptionType: SubscriptionType.Events,
};

export class EventsDomain extends TransformDomain<Event, EventDto> {
  resource = "Event";
  cacheKey = "events";
  pk = "key";

  public deserialize = dtoToEvent;
  public serialize = eventToDto;

  watchWs$: Source<Event[]> = pipe(
    merge([
      this.ws.subscription$$(EventsSubscription),
      // Events can be included in assist payloads
      this.ws.subscription$$({ subscriptionType: SubscriptionType.AssistPlanned }),
      this.ws.subscription$$({ subscriptionType: SubscriptionType.AssistCompleted }),
    ]),
    filter((envelope) => !!envelope.data),
    map((envelope) => {
      return envelope.type === SubscriptionType.Events
        ? (envelope.data as EventDto[])
        : ((envelope.data as AssistCompleted).events as EventDto[]);
    }),
    buffer(interval(10)),
    map((buffered) => buffered.flat()),
    deserialize(this.deserialize)
  );

  watchAll$ = pipe(
    merge([this.upsert$, this.watchWs$]),
    map((items) => this.patchExpectedChanges(items))
  );

  watch$$ = (start?: Date, end?: Date) => {
    const subscription = {
      subscriptionType: SubscriptionType.Events,
      startTime: start,
      endTime: end,
    };

    return pipe(
      merge([
        this.upsert$,
        pipe(
          merge([
            this.ws.subscription$$(subscription),
            // Events can be included in assist payloads
            this.ws.subscription$$({ subscriptionType: SubscriptionType.AssistPlanned }),
            this.ws.subscription$$({ subscriptionType: SubscriptionType.AssistCompleted }),
          ]),
          filter((envelope) => !!envelope.data),
          map((envelope) => {
            return envelope.type === SubscriptionType.Events
              ? (envelope.data as EventDto[])
              : ((envelope.data as AssistCompleted).events as EventDto[]);
          }),
          buffer(interval(10)),
          map((buffered) => buffered.flat()),
          deserialize(this.deserialize)
        ),
      ]),
      map((items) => this.patchExpectedChanges(items))
    );
  };

  list$$ = (options: EventListOptions) => {
    return pipe(
      fromPromise(this.client.events.list(options)),
      map((items) => this.patchExpectedChanges(items))
    );
  };

  listAndWatch$$ = ({ start, end, statuses = [EventStatus.Published], ...options }: ListAndWatchOptions) => {
    return pipe(
      concat<Event[] | null>([this.list$$(options), this.watch$$(start, end)]),
      upsert((e) => this.getPk(e)),
      map(eventsQueryFilter({ start, end, statuses, ...options }))
    );
  };

  watchId$$ = (key: string) => {
    return pipe(
      concat([fromPromise(this.get(key).then((r) => (!!r ? [r] : []))), this.watchAll$]),
      map((items) => items?.find((i) => i.key === key))
    );
  };

  watchQuery$$ = ({ statuses = [EventStatus.Published], ...options }: WatchQueryOptions) => {
    return pipe(
      this.watch$$(options.start, options.end),
      upsert((e) => this.getPk(e)),
      map(eventsQueryFilter({ statuses, ...options }))
    );
  };

  list = this.deserializeResponse(
    ({ start, end, type, sourceDetails, habitIds, oneOnOneIds, calendarIds }: EventListOptions) => {
      return this.api.events.query({
        start: start ? format(start, "yyyy-MM-dd") : undefined,
        end: end ? format(end, "yyyy-MM-dd") : undefined,
        type: type || undefined,
        sourceDetails: sourceDetails,
        habitIds,
        calendarIds,
        recurringOneOnOneIds: oneOnOneIds,
      });
    }
  );

  listPersonal = this.deserializeResponse((start?: Date, end?: Date, limit?: number) => {
    return this.api.events.getPersonal1({
      start: start ? format(start, "yyyy-MM-dd") : undefined,
      end: end ? format(end, "yyyy-MM-dd") : undefined,
      limit,
    });
  });

  get = this.deserializeResponse((eventId: string, calendarId?: number, sourceDetails?: boolean) => {
    const query = { sourceDetails };
    return undefined !== calendarId
      ? this.api.events.getForCalendar(calendarId, eventId, query)
      : this.api.events.get1(eventId, query);
  });

  adjustTravelTime = (
    calendarId: number,
    eventId: string,
    type: typeof AssistType.PreTravel | typeof AssistType.PostTravel,
    duration: number
  ) => {
    const notificationKey = this.generateUid("adjustTravelTime", `${calendarId}/${eventId}`);

    this.addNotificationKey(notificationKey);

    return this.api.events
      .adjustTravelTime(calendarId, eventId, type.key, { duration, notificationKey }, {})
      .then((res) => {
        this.updateNotificationKey(notificationKey, NotificationKeyStatus.Requested);
        return res;
      })
      .catch((reason) => {
        console.warn("Request failed, clearing notification key", notificationKey, reason);
        this.updateNotificationKey(notificationKey, NotificationKeyStatus.Failed);
        throw reason;
      });
  };

  adjustConfBuffer = (calendarId: number, eventId: string, duration: number) => {
    const notificationKey = this.generateUid("adjustConfBuffer", `${calendarId}/${eventId}`);

    this.addNotificationKey(notificationKey);

    return this.api.events
      .adjustConferenceBuffer(calendarId, eventId, { duration, notificationKey }, {})
      .then((res) => {
        this.updateNotificationKey(notificationKey, NotificationKeyStatus.Requested);
        return res;
      })
      .catch((reason) => {
        console.warn("Request failed, clearing notification key", notificationKey, reason);
        this.updateNotificationKey(notificationKey, NotificationKeyStatus.Failed);
        throw reason;
      });
  };

  adjustPriority = (calendarId: number, eventId: string, priority: PriorityLevel) => {
    const notificationKey = this.generateUid("adjustPriority", `${calendarId}/${eventId}`);

    this.addNotificationKey(notificationKey);

    return this.api.events
      .adjustPriority(calendarId, eventId, { priority: priority as PriorityLevelDto, notificationKey }, {})
      .then((res) => {
        this.updateNotificationKey(notificationKey, NotificationKeyStatus.Requested);
        return res;
      })
      .catch((reason) => {
        console.warn("Request failed, clearing notification key", notificationKey, reason);
        this.updateNotificationKey(notificationKey, NotificationKeyStatus.Failed);
        throw reason;
      });
  };
}

export const findNextTemporalThing = <T>(
  things: T[],
  now: Date = new Date(),
  getBounds: (thing: T) => [start: Date, end: Date]
): T | undefined => {
  let nextStart: Date;

  return things.reduce(
    (nextThing, thing) => {
      const [thingStart, thingEnd] = getBounds(thing);
      if (
        (!nextThing && thingEnd > now) ||
        (!!thingEnd && thingEnd > now && !!thingStart && !!nextStart && thingStart < nextStart)
      ) {
        nextStart = thingStart;
        nextThing = thing;
      }

      return nextThing;
    },
    undefined as undefined | T
  );
};

export const findNextUpcomingCalendarEvent = (events: CalendarEvent[], now?: Date) =>
  findNextTemporalThing(events, now, ({ startTime, endTime }) => [startTime, endTime]);

export const findNextUpcomingEvent = (events: Event[], now?: Date) =>
  findNextTemporalThing(events, now, ({ eventStart, eventEnd }) => [eventStart, eventEnd]);
